/**
 * Toolbar chrome for the Units tab — mirrors the accepted catalog chrome
 * (`apps/web/src/routes/media-units/index.tsx` +
 * `apps/web/src/features/media-units/components/inventory-toolbar-row.tsx`):
 *
 *   Row 1 — `FilterPanel` with the search box inline via `leadingContent`, then filter
 *           pills, "Dodaj filtr" and "Wyczyść wszystkie". No separate "Filtry" toggle
 *           button and no standalone search row.
 *   Row 2 — one dense, right-aligned action row: icon-only buttons
 *           (`IconButtonWithTooltip` / `ColumnPicker` at `icon-sm`), then icon+text
 *           buttons at `size="sm"`.
 *
 * See `apps/mockups/src/screens/components/offer-workspace/units-tab-toolbar-row.mockup.tsx`
 * for the approved reference this file matches.
 */
import * as React from 'react';
import {
  Search,
  MapPin,
  Package,
  Sparkles,
  ChevronDown,
  Map,
  CalendarRange,
  X,
} from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import { Popover, PopoverContent, PopoverTrigger } from '@ooh/ui/components/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@ooh/ui/components/tooltip';
import { IconButtonWithTooltip } from '@ooh/ui/components/icon-button-with-tooltip';
import { ColumnPicker, type ColumnPickerColumn } from '@ooh/ui/components/column-picker';
import { FilterPanel, PillWrapper } from '@ooh/ui/components/filter-panel';
import { PILL_BUTTON_BASE, PILL_BUTTON_ACTIVE } from '@ooh/ui/components/filter-pill-primitives';
import { useTranslation } from 'react-i18next';
import { InventorySearchInput } from '../../../components/inventory-search-input';
import { OFFER_TABLE_PINNED_COLUMN_IDS } from './units-tab-columns';
import type { UnitsTableSortKey } from './units-tab-columns';

export type SortKey = UnitsTableSortKey;
export type SortDir = 'asc' | 'desc';

// ─── Active-sort chip ───────────────────────────────────────────
//
// Shown only while a column-header sort is active (the default `sortOrder`
// key has no backing column, so it never renders a chip). Reuses the same
// pill-clear visual language as FilterPanel's active filter pills
// (filter-pill-primitives.tsx) instead of inventing a new chip style.

export interface ActiveSortInfo {
  /** The sorted column's display label, e.g. "Miasto". */
  columnLabel: string;
  dir: SortDir;
}

function SortChip({
  onClear,
  label,
  clearAriaLabel,
}: {
  onClear: () => void;
  label: string;
  clearAriaLabel: string;
}) {
  return (
    <div className={`${PILL_BUTTON_BASE} ${PILL_BUTTON_ACTIVE}`} data-testid="units-tab-sort-chip">
      <span className="text-[11px] text-foreground">{label}</span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={clearAriaLabel}
        data-testid="units-tab-sort-chip-clear"
        onClick={onClear}
        className="h-4 w-4 shrink-0 rounded-full p-0.5 hover:opacity-70"
      >
        <X className="h-3 w-3 text-foreground/60" />
      </Button>
    </div>
  );
}

type FilterPanelPassthroughProps = Pick<
  React.ComponentProps<typeof FilterPanel>,
  'fields' | 'value' | 'onChange' | 'labels' | 'rangeLabels'
>;

interface UnitsTabToolbarProps extends FilterPanelPassthroughProps {
  canEdit: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  searchAriaLabel: string;
  onOpenAddDrawer?: () => void;
  /** When provided, the map mode button activates the full-screen map overlay */
  onOpenMapMode?: () => void;
  /** Columns for the "Kolumny" picker, in user-defined order (structural columns included). */
  columnPickerColumns: ColumnPickerColumn[];
  hiddenColumnIds: ReadonlySet<string>;
  onToggleColumn: (id: string) => void;
  onMoveColumnUp?: (id: string) => void;
  onMoveColumnDown?: (id: string) => void;
  /**
   * The offer's true unfiltered unit count. Pass only when a filter is
   * active and narrower than the offer total — `undefined` renders the plain
   * `unitCount` (used when unfiltered, where `unitCount` already IS the total).
   */
  grandTotal?: number;
  unitCount: number;
  isMapVisible: boolean;
  onToggleMap: () => void;
  mapTooltip: string;
  showAvailability: boolean;
  onToggleAvailability: () => void;
  availabilityTooltip: string;
  /** Set only while a column-header sort is active; renders the clearable sort chip. */
  activeSort?: ActiveSortInfo;
  onClearSort?: () => void;
  /**
   * The always-visible bulk-discount action (`BulkDiscountPopover`), rendered
   * inline in this toolbar rather than only when rows are selected — both of
   * its uses survive: with rows selected it applies to that selection, with
   * nothing selected it applies to every unit in the offer
   * (offer-unified-table capability).
   */
  discountAction?: React.ReactNode;
  /** Icon-only "Pobierz specyfikacje" trigger (`OfferBulkDownloadTrigger` with `iconOnly`), omitted when bulk download isn't wired. */
  bulkDownloadAction?: React.ReactNode;
}

export function UnitsTabToolbar({
  canEdit,
  search,
  onSearchChange,
  searchPlaceholder,
  searchAriaLabel,
  fields,
  value,
  onChange,
  labels,
  rangeLabels,
  onOpenAddDrawer,
  onOpenMapMode,
  columnPickerColumns,
  hiddenColumnIds,
  onToggleColumn,
  onMoveColumnUp,
  onMoveColumnDown,
  grandTotal,
  unitCount,
  isMapVisible,
  onToggleMap,
  mapTooltip,
  showAvailability,
  onToggleAvailability,
  availabilityTooltip,
  activeSort,
  onClearSort,
  discountAction,
  bulkDownloadAction,
}: UnitsTabToolbarProps) {
  const { t } = useTranslation('offers');

  const comingSoonLabel = t('workspace.unitsTab.toolbar.addModes.comingSoon');

  const addUnitModes = [
    {
      id: 'search' as const,
      icon: Search,
      label: t('workspace.unitsTab.toolbar.addModes.search'),
      /** search is accessible via the primary button; keep dropdown entry disabled */
      showComingSoonTooltip: false,
    },
    {
      id: 'map' as const,
      icon: MapPin,
      label: t('workspace.unitsTab.toolbar.addModes.map'),
      showComingSoonTooltip: onOpenMapMode === undefined,
      onClick: onOpenMapMode,
    },
    {
      id: 'package' as const,
      icon: Package,
      label: t('workspace.unitsTab.toolbar.addModes.package'),
      showComingSoonTooltip: true,
    },
    {
      id: 'ai' as const,
      icon: Sparkles,
      label: t('workspace.unitsTab.toolbar.addModes.ai'),
      showComingSoonTooltip: true,
    },
  ];

  const unitCountLabel =
    grandTotal !== undefined
      ? t('workspace.unitsTab.toolbar.unitCountOfTotal', { count: unitCount, total: grandTotal })
      : t('workspace.unitsTab.toolbar.unitCount', { count: unitCount });

  const sortChipLabel = activeSort
    ? t('workspace.unitsTab.toolbar.sortChipLabel', {
        column: activeSort.columnLabel,
        direction: activeSort.dir === 'asc' ? '↑' : '↓',
      })
    : '';

  return (
    <div className="shrink-0 border-b border-border bg-background px-4 py-2">
      {/* ── Row 1: search + filter pills (catalog parity) ───────── */}
      <FilterPanel
        leadingContent={
          <PillWrapper label="&nbsp;" showLabel={false}>
            <InventorySearchInput
              value={search}
              onChange={onSearchChange}
              placeholder={searchPlaceholder}
              ariaLabel={searchAriaLabel}
            />
          </PillWrapper>
        }
        fields={fields}
        value={value}
        onChange={onChange}
        labels={labels}
        rangeLabels={rangeLabels}
      />

      {/* ── Row 2: dense right-aligned action row (catalog parity) ─ */}
      <div className="mt-3 flex items-center gap-2" data-testid="table-action-row">
        <span
          className="text-[11px] font-medium text-muted-foreground"
          data-testid="units-tab-unit-count"
        >
          {unitCountLabel}
        </span>

        <div className="ml-auto flex items-center gap-2">
          {activeSort && onClearSort ? (
            <SortChip
              onClear={onClearSort}
              label={sortChipLabel}
              clearAriaLabel={t('workspace.unitsTab.toolbar.sortChipClearAriaLabel')}
            />
          ) : null}
          <IconButtonWithTooltip
            icon={Map}
            tooltip={mapTooltip}
            active={isMapVisible}
            onClick={onToggleMap}
            testId="offer-units-map-toggle"
          />
          <IconButtonWithTooltip
            icon={CalendarRange}
            tooltip={availabilityTooltip}
            active={showAvailability}
            onClick={onToggleAvailability}
            testId="offer-units-availability-toggle"
          />
          <ColumnPicker
            columns={columnPickerColumns}
            pinnedIds={OFFER_TABLE_PINNED_COLUMN_IDS}
            hiddenColumnIds={hiddenColumnIds}
            onToggle={onToggleColumn}
            onMoveUp={onMoveColumnUp}
            onMoveDown={onMoveColumnDown}
            label={t('workspace.unitsTab.columnVisibility.toggleButton')}
            triggerSize="icon-sm"
            sectionLabel={t('workspace.unitsTab.columnVisibility.sectionLabel')}
            searchPlaceholder={t('workspace.unitsTab.columnVisibility.searchPlaceholder')}
            noResults={t('workspace.unitsTab.columnVisibility.noResults')}
          />
          {bulkDownloadAction}

          {discountAction}

          {canEdit ? (
            <Popover>
              <div className="flex items-center overflow-hidden rounded-lg shadow-sm">
                {/* variant="secondary" — "Dodaj nośniki" is a routine, non-destructive action;
                    the default red variant is reserved for critical/dangerous actions
                    (CU-86caypxpc). */}
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={onOpenAddDrawer}
                  aria-label={t('workspace.unitsTab.toolbar.addUnitsPrimaryAriaLabel')}
                  className="rounded-r-none"
                  data-testid="add-units-button"
                >
                  <Search className="h-4 w-4" />
                  {t('workspace.unitsTab.toolbar.addUnits')}
                </Button>
                <div className="h-8 w-px shrink-0 bg-border" />
                <PopoverTrigger asChild>
                  <Button
                    size="sm"
                    variant="secondary"
                    type="button"
                    aria-label={t('workspace.unitsTab.toolbar.addUnitsMenuAriaLabel')}
                    className="rounded-l-none px-1.5"
                    data-testid="add-units-menu-trigger"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
              </div>
              <PopoverContent align="end" className="w-52 p-1">
                <TooltipProvider delayDuration={300}>
                  {addUnitModes.map((mode) => {
                    const Icon = mode.icon;
                    const isEnabled = 'onClick' in mode && mode.onClick !== undefined;
                    const button = (
                      <Button
                        key={mode.id}
                        variant="ghost"
                        size="xs"
                        disabled={!isEnabled}
                        onClick={isEnabled ? mode.onClick : undefined}
                        className="w-full justify-start rounded-sm px-2 py-1.5 text-[13px] text-muted-foreground disabled:opacity-100"
                        data-testid={`add-units-mode-${mode.id}`}
                      >
                        <Icon className="h-4 w-4 shrink-0" />
                        <span>{mode.label}</span>
                      </Button>
                    );

                    if (!mode.showComingSoonTooltip) return button;

                    return (
                      <Tooltip key={mode.id}>
                        <TooltipTrigger asChild>
                          <span className="block w-full">{button}</span>
                        </TooltipTrigger>
                        <TooltipContent side="left">{comingSoonLabel}</TooltipContent>
                      </Tooltip>
                    );
                  })}
                </TooltipProvider>
              </PopoverContent>
            </Popover>
          ) : null}
        </div>
      </div>
    </div>
  );
}
